import { Toast } from '@gimlite/ui-v2/components/toast/toast.component';
import { message } from 'antd';
import { MessageInstance, NoticeType } from 'antd/es/message/interface';
import { action, makeAutoObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Slide, ToastContainer, toast } from 'react-toastify';
import { logWithState } from '../functions/console.function';
import { useStoreCommon } from '../hooks/useStore.hook';
import { RootStoreMobX } from './root.store';

export const mapMode = {
  ERROR: 'error',
  SUCCESS: 'success',
  INFO: 'info',
  WARNING: 'warning',
  LOADING: 'loading',
} as const satisfies Record<NotificationModeType, NoticeType>;

export type NotificationModeType =
  | 'ERROR'
  | 'WARNING'
  | 'SUCCESS'
  | 'INFO'
  | 'LOADING';
export type NotificationContentType = string;

export type NotificationStoreContextType = {
  notif: {
    mode: NotificationModeType;
    content: NotificationContentType;
    key?: string;
  };
  instance: MessageInstance;
  ui: 'v1' | 'v2';
};

export class NotificationStoreMobX {
  rootStore: RootStoreMobX;
  @observable public load: boolean = false;
  @observable public instance: NotificationStoreContextType['instance'] | null =
    null;
  @observable public ui: NotificationStoreContextType['ui'] | null = null;
  constructor(rootStore: RootStoreMobX) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  @action public init({
    instance,
    ui,
  }: {
    instance: MessageInstance;
    ui: NotificationStoreContextType['ui'] | null;
  }) {
    runInAction(() => {
      this.load = false;
      this.instance = instance;
      this.ui = ui;
      this.load = true;
    });
  }

  @action public emit({
    mode,
    content,
    key,
    duration,
  }: {
    mode: NotificationModeType;
    content: NotificationContentType;
    key?: string;
    duration?: number;
  }) {
    console.log('emit', {
      mode,
      content,
      key,
      duration,
      ui: this.ui,
    });
    if (this.ui) {
      const toastId = key;
      const autoClose = duration ? duration * 1000 : 4000;

      switch (this.ui) {
        case 'v1':
          if (!this.instance) return;

          this.instance.open({
            duration: duration || 3,
            type: mapMode[mode],
            content,
            style: {
              textAlign: 'end',
              marginRight: '10px',
            },
            key,
          });
          break;
        case 'v2':
          toast(
            <Toast
              key={toastId}
              description={content}
              variant={mapMode[mode]}
            />,
            {
              toastId,
              autoClose,
              position: 'bottom-left',
              draggable: true,
              pauseOnHover: true,
              closeButton: false,
              hideProgressBar: true,
              transition: Slide,
              style: {
                padding: '0px',
                zIndex: 99999,
              },
            },
          );
      }

      return;
    }
  }

  @action public destroy(key?: string) {
    switch (this.ui) {
      case 'v1':
        if (!this.instance) return;
        this.instance.destroy(key);
        break;
      case 'v2':
        if (key) {
          toast.dismiss(key);
        } else {
          toast.dismiss();
        }
        break;
    }
  }
}

export const ConfigNotification = observer(
  ({
    children,
    ui = 'v1',
  }: {
    children: React.ReactNode;
    ui?: NotificationStoreContextType['ui'] | null;
  }) => {
    const [messageApi, contextHolder] = message.useMessage();
    const { NotificationStore } = useStoreCommon();

    useEffect(() => {
      if (!NotificationStore.load && messageApi) {
        logWithState({ state: 'INFO', value: 'ConfigNotification init' });
        NotificationStore.init({ instance: messageApi, ui });
      }

      if (NotificationStore.load) {
        logWithState({ state: 'INFO', value: 'ConfigNotification load' });
      }
    }, [NotificationStore.load, messageApi, ui]);

    if (!NotificationStore.load) return null;

    return (
      <>
        {ui === 'v1' ? (
          contextHolder
        ) : (
          <ToastContainer style={{ zIndex: 99999 }} />
        )}
        {children}
      </>
    );
  },
);
